import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Autocomplete,
  FormHelperText,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Badge,
  Tooltip,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  Route as RouteIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Tune as OptimizeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  Map as MapIcon,
  DirectionsWalk as DirectionsWalkIcon,
  DirectionsCar as DirectionsCarIcon,
  DirectionsBike as DirectionsBikeIcon
} from '@mui/icons-material';
import { useCompany } from '../context/CompanyContext';
import stockService from '../services/stockService';
import './PickListManagement.css';

const PickListManagement = () => {
  const { companyId } = useCompany();
  const [pickLists, setPickLists] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [pickers, setPickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPickList, setEditingPickList] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterPicker, setFilterPicker] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [optimizationDialog, setOptimizationDialog] = useState(false);
  const [optimizationType, setOptimizationType] = useState('route');
  const [optimizationProgress, setOptimizationProgress] = useState(0);

  const [formData, setFormData] = useState({
    // Temel Bilgiler
    name: '',
    description: '',
    warehouseId: '',
    pickerId: '',
    priority: 'normal',
    estimatedDuration: 0,
    
    // Siparişler
    orderIds: [],
    
    // Ürünler
    items: [],
    
    // Durum
    status: 'pending',
    startTime: null,
    endTime: null,
    actualDuration: 0,
    
    // Optimizasyon
    optimizationSettings: {
      routeOptimization: true,
      priorityBased: true,
      zoneBased: true,
      timeWindow: 30,
      maxItemsPerTrip: 50,
      vehicleType: 'walking'
    },
    
    // İstatistikler
    statistics: {
      totalItems: 0,
      totalQuantity: 0,
      totalWeight: 0,
      totalVolume: 0,
      estimatedDistance: 0,
      actualDistance: 0,
      efficiency: 0
    },
    
    // Notlar
    notes: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // API çağrıları
      const [warehousesData, ordersData, productsData] = await Promise.all([
        stockService.getWarehouses(),
        stockService.getOrders(), // Bu API'yi eklemek gerekebilir
        stockService.getProducts()
      ]);
      
      setWarehouses(warehousesData);
      setOrders(ordersData || []);
      setProducts(productsData);
      
      // Toplayıcılar için mock data (gerçek API'de bu da olacak)
      setPickers([
        { id: 1, name: 'Ahmet Yılmaz', status: 'available' },
        { id: 2, name: 'Mehmet Demir', status: 'busy' },
        { id: 3, name: 'Fatma Kaya', status: 'available' }
      ]);
      
      await loadPickLists();
    } catch (error) {
      showSnackbar('Veri yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadPickLists = async () => {
    try {
      const pickListsData = await stockService.getPickLists();
      setPickLists(pickListsData);
    } catch (error) {
      showSnackbar('Toplama listeleri yüklenirken hata oluştu', 'error');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editingPickList) {
        await stockService.updatePickList(editingPickList.id, formData);
        showSnackbar('Toplama listesi başarıyla güncellendi', 'success');
      } else {
        await stockService.createPickList(formData);
        showSnackbar('Toplama listesi başarıyla oluşturuldu', 'success');
      }
      setOpenDialog(false);
      resetForm();
      await loadPickLists();
    } catch (error) {
      showSnackbar('Toplama listesi kaydedilirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (pickListId) => {
    if (window.confirm('Bu toplama listesini silmek istediğinizden emin misiniz?')) {
      setLoading(true);
      try {
        await stockService.deletePickList(pickListId);
        showSnackbar('Toplama listesi başarıyla silindi', 'success');
        await loadPickLists();
      } catch (error) {
        showSnackbar('Toplama listesi silinirken hata oluştu', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOptimize = async () => {
    setOptimizationDialog(true);
    setOptimizationProgress(0);
    
    // Simüle edilmiş optimizasyon süreci
    const interval = setInterval(() => {
      setOptimizationProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          showSnackbar('Toplama listesi başarıyla optimize edildi', 'success');
          setOptimizationDialog(false);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  const handleStartPicking = async (pickListId) => {
    setLoading(true);
    try {
      await stockService.startPicking(pickListId);
      showSnackbar('Toplama işlemi başlatıldı', 'success');
      await loadPickLists();
    } catch (error) {
      showSnackbar('Toplama başlatılırken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletePicking = async (pickListId) => {
    setLoading(true);
    try {
      await stockService.completePicking(pickListId);
      showSnackbar('Toplama işlemi tamamlandı', 'success');
      await loadPickLists();
    } catch (error) {
      showSnackbar('Toplama tamamlanırken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      warehouseId: '',
      pickerId: '',
      priority: 'normal',
      estimatedDuration: 0,
      orderIds: [],
      items: [],
      status: 'pending',
      startTime: null,
      endTime: null,
      actualDuration: 0,
      optimizationSettings: {
        routeOptimization: true,
        priorityBased: true,
        zoneBased: true,
        timeWindow: 30,
        maxItemsPerTrip: 50,
        vehicleType: 'walking'
      },
      statistics: {
        totalItems: 0,
        totalQuantity: 0,
        totalWeight: 0,
        totalVolume: 0,
        estimatedDistance: 0,
        actualDistance: 0,
        efficiency: 0
      },
      notes: ''
    });
    setEditingPickList(null);
    setActiveTab(0);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'default';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Bekliyor';
      case 'in_progress': return 'Devam Ediyor';
      case 'completed': return 'Tamamlandı';
      case 'cancelled': return 'İptal Edildi';
      default: return 'Bilinmiyor';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high': return 'Yüksek';
      case 'normal': return 'Normal';
      case 'low': return 'Düşük';
      default: return 'Bilinmiyor';
    }
  };

  const filteredPickLists = pickLists.filter(pickList => {
    const matchesSearch = pickList.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pickList.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || pickList.status === filterStatus;
    const matchesWarehouse = !filterWarehouse || pickList.warehouse === filterWarehouse;
    const matchesPicker = !filterPicker || pickList.picker === filterPicker;
    
    return matchesSearch && matchesStatus && matchesWarehouse && matchesPicker;
  });

  const renderBasicInfoTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Toplama Listesi Adı"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          required
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Açıklama"
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Depo</InputLabel>
          <Select
            value={formData.warehouseId}
            onChange={(e) => setFormData({...formData, warehouseId: e.target.value})}
          >
            {warehouses.map(warehouse => (
              <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Toplayıcı</InputLabel>
          <Select
            value={formData.pickerId}
            onChange={(e) => setFormData({...formData, pickerId: e.target.value})}
          >
            {pickers.map(picker => (
              <MenuItem key={picker.id} value={picker.id}>{picker.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Öncelik</InputLabel>
          <Select
            value={formData.priority}
            onChange={(e) => setFormData({...formData, priority: e.target.value})}
          >
            <MenuItem value="low">Düşük</MenuItem>
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="high">Yüksek</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Tahmini Süre (Dakika)"
          value={formData.estimatedDuration}
          onChange={(e) => setFormData({...formData, estimatedDuration: parseInt(e.target.value) || 0})}
        />
      </Grid>
    </Grid>
  );

  const renderOrdersTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Sipariş Seçimi
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Siparişler</InputLabel>
          <Select
            multiple
            value={formData.orderIds}
            onChange={(e) => setFormData({...formData, orderIds: e.target.value})}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const order = orders.find(o => o.id === value);
                  return (
                    <Chip key={value} label={order?.orderNumber || value} size="small" />
                  );
                })}
              </Box>
            )}
          >
            {orders.map((order) => (
              <MenuItem key={order.id} value={order.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span>{order.orderNumber}</span>
                  <span style={{ color: '#666' }}>{order.customerName}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Seçili Siparişler
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Sipariş No</TableCell>
                <TableCell>Müşteri</TableCell>
                <TableCell>Öncelik</TableCell>
                <TableCell>Ürün Sayısı</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.orderIds.map((orderId) => {
                const order = orders.find(o => o.id === orderId);
                return order ? (
                  <TableRow key={order.id}>
                    <TableCell>{order.orderNumber}</TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell>
                      <Chip
                        label={getPriorityText(order.priority)}
                        color={getPriorityColor(order.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{order.items}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            orderIds: formData.orderIds.filter(id => id !== order.id)
                          });
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ) : null;
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );

  const renderOptimizationTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Optimizasyon Ayarları
        </Typography>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.optimizationSettings.routeOptimization}
              onChange={(e) => setFormData({
                ...formData,
                optimizationSettings: {
                  ...formData.optimizationSettings,
                  routeOptimization: e.target.checked
                }
              })}
            />
          }
          label="Rota Optimizasyonu"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.optimizationSettings.priorityBased}
              onChange={(e) => setFormData({
                ...formData,
                optimizationSettings: {
                  ...formData.optimizationSettings,
                  priorityBased: e.target.checked
                }
              })}
            />
          }
          label="Öncelik Bazlı Sıralama"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.optimizationSettings.zoneBased}
              onChange={(e) => setFormData({
                ...formData,
                optimizationSettings: {
                  ...formData.optimizationSettings,
                  zoneBased: e.target.checked
                }
              })}
            />
          }
          label="Bölge Bazlı Gruplama"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Araç Tipi</InputLabel>
          <Select
            value={formData.optimizationSettings.vehicleType}
            onChange={(e) => setFormData({
              ...formData,
              optimizationSettings: {
                ...formData.optimizationSettings,
                vehicleType: e.target.value
              }
            })}
          >
            <MenuItem value="walking">Yürüyerek</MenuItem>
            <MenuItem value="cart">El Arabası</MenuItem>
            <MenuItem value="forklift">Forklift</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Zaman Penceresi (Dakika)"
          value={formData.optimizationSettings.timeWindow}
          onChange={(e) => setFormData({
            ...formData,
            optimizationSettings: {
              ...formData.optimizationSettings,
              timeWindow: parseInt(e.target.value) || 30
            }
          })}
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Maksimum Ürün/Tur"
          value={formData.optimizationSettings.maxItemsPerTrip}
          onChange={(e) => setFormData({
            ...formData,
            optimizationSettings: {
              ...formData.optimizationSettings,
              maxItemsPerTrip: parseInt(e.target.value) || 50
            }
          })}
        />
      </Grid>
    </Grid>
  );

  const tabPanels = [
    { label: 'Temel Bilgiler', icon: <AssignmentIcon />, content: renderBasicInfoTab() },
    { label: 'Siparişler', icon: <ShippingIcon />, content: renderOrdersTab() },
    { label: 'Optimizasyon', icon: <OptimizeIcon />, content: renderOptimizationTab() }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Toplama Listesi Yönetimi
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<OptimizeIcon />}
              onClick={handleOptimize}
            >
              Optimize Et
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
            >
              Yeni Toplama Listesi
            </Button>
          </Box>
        </Box>

        {/* Arama ve Filtreler */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Toplama listesi ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Durum</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">Tümü</MenuItem>
                <MenuItem value="pending">Bekliyor</MenuItem>
                <MenuItem value="in_progress">Devam Ediyor</MenuItem>
                <MenuItem value="completed">Tamamlandı</MenuItem>
                <MenuItem value="cancelled">İptal Edildi</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Depo</InputLabel>
              <Select
                value={filterWarehouse}
                onChange={(e) => setFilterWarehouse(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                {warehouses.map(warehouse => (
                  <MenuItem key={warehouse.id} value={warehouse.name}>{warehouse.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Toplayıcı</InputLabel>
              <Select
                value={filterPicker}
                onChange={(e) => setFilterPicker(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                {pickers.map(picker => (
                  <MenuItem key={picker.id} value={picker.name}>{picker.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Toplama Listesi Tablosu */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Toplama Listesi</TableCell>
                <TableCell>Depo</TableCell>
                <TableCell>Toplayıcı</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>Öncelik</TableCell>
                <TableCell>Ürün Sayısı</TableCell>
                <TableCell>Verimlilik</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPickLists.map((pickList) => (
                <TableRow key={pickList.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">{pickList.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pickList.description}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{pickList.warehouse}</TableCell>
                  <TableCell>{pickList.picker}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(pickList.status)}
                      color={getStatusColor(pickList.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPriorityText(pickList.priority)}
                      color={getPriorityColor(pickList.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">{pickList.totalItems}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pickList.totalQuantity} adet
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="primary">
                        %{pickList.efficiency}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pickList.actualDuration}/{pickList.estimatedDuration} dk
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {pickList.status === 'pending' && (
                        <Tooltip title="Toplamayı Başlat">
                          <IconButton
                            size="small"
                            onClick={() => handleStartPicking(pickList.id)}
                            color="success"
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {pickList.status === 'in_progress' && (
                        <Tooltip title="Toplamayı Tamamla">
                          <IconButton
                            size="small"
                            onClick={() => handleCompletePicking(pickList.id)}
                            color="success"
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <Tooltip title="Düzenle">
                        <IconButton
                          size="small"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(pickList.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Toplama Listesi Ekleme/Düzenleme Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingPickList ? 'Toplama Listesi Düzenle' : 'Yeni Toplama Listesi'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabPanels.map((tab, index) => (
                <Tab
                  key={index}
                  label={tab.label}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>
          
          <Box sx={{ mt: 2 }}>
            {tabPanels[activeTab].content}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            İptal
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {loading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Optimizasyon Dialog */}
      <Dialog
        open={optimizationDialog}
        onClose={() => setOptimizationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Toplama Listesi Optimizasyonu
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Toplama listeleri optimize ediliyor...
            </Typography>
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={optimizationProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                %{optimizationProgress} tamamlandı
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PickListManagement;
